import { Injectable, TemplateRef, ViewContainerRef, ReflectiveInjector } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef, MdSidenav } from '@angular/material';
import { CfDialogComponent } from '../../components/dialog/dialog.component';

const /** @type {?} */ LIMIT_RANGE = 300;
/**
 * <p>Component Template Service.</p>
 */
export class DialogService {
  dialogRef: MdDialogRef<any>;
  dialogContainer: any;
  dialogOptions: any;
/**
 * @param {?} dialog
 */
constructor(public dialog: MdDialog) {}
/**
 * <p>Method to show template or text in dialog window.</p>
 * <p>Parameter options is an object of structure:
 * { template: TemplateRef<any>, text: string, dialogOptions: any }.
 * Note that if text is present, template will be ignored.</p>
 * @param {?} options
 * @return {?}
 */
show(options): void {
    this.dialogOptions = options;
    this.openDialog();
    this.insertContentToDialog(options);
    this.setDialogCallbacks();
  }
/**
 * <p>Method to show template or text in floating dialog, next to the cursor where user clicked.</p>
 * <p>Parameter options is an object of structure:
 * { template: TemplateRef<any>, text: string, dialogOptions: any }.
 * Note that if text is present, template will be ignored.</p>
 * @param {?} options
 * @return {?}
 */
showFloating(options): void {
    this.dialogOptions = options;

    this.openFloatingDialog();

    this.setFloatingDialogBackdropElement();
    this.setFloatingDialogContainers();
    this.setFloatingDialogContainerPosition();

    this.insertContentToDialog(options);
    this.setDialogCallbacks();
  }
/**
 * @return {?}
 */
close() {
    this.dialogRef.close();
  }
/**
 * @param {?=} isFloating
 * @return {?}
 */
private openDialog(isFloating?: boolean) {
    let /** @type {?} */ config = new MdDialogConfig();
    config['width'] = this.dialogOptions.properties.width;
    config['height'] = this.dialogOptions.properties.height;
    config['disableClose'] = this.dialogOptions.properties.disableClose;
    // config['position'] = "absolute";

    this.dialogRef = this.dialog.open(CfDialogComponent, config);
    this.dialogContainer = this.getDialogContainer();

    // set relative position to default normal (not floating) dialog panel
    this.getDialogPane().style.position = 'relative';

    this.setDialogContainerInComponentPosition();
  }
/**
 * @return {?}
 */
private openFloatingDialog() {
    // must be for floating window, to close it by clicking on MdDialog backdropElement
    this.dialogOptions.properties['disableClose'] = false;

    this.openDialog(true);
  }
/**
 * @return {?}
 */
private setFloatingDialogBackdropElement() {
    let /** @type {?} */ backdropElement = this.getDialogBackdropElement();
    backdropElement.style.position = 'fixed';
    backdropElement.style.opacity = '0';

    // to catch click event to close dialog
    backdropElement.style.zIndex = -1;
    backdropElement.style.left = '0px';
    backdropElement.style.top = '0px';
    backdropElement.style.bottom = '0px';
    backdropElement.style.right = '0px';
  }
/**
 * @return {?}
 */
private setFloatingDialogContainers() {
    let /** @type {?} */ dialogPaneWrapper = this.getDialogPaneWrapper();
    dialogPaneWrapper.style.display = 'block';
    dialogPaneWrapper.style.position = 'static';

    let /** @type {?} */ dialogContainer = dialogPaneWrapper.parentElement;
    if (!this.dialogOptions.properties.width || this.dialogOptions.properties.width.indexOf('%') === -1) {
      dialogContainer.style.width = 'auto';
    }
    dialogContainer.style.height = 'auto';
    dialogContainer.style.position = 'absolute';
  }
/**
 * @return {?}
 */
private setFloatingDialogContainerPosition() {
    if (this.dialogOptions.properties.position === 'auto') {
      this.setFloatingDialogContainerAutoPosition();
    } else {
      this.setFloatingDialogContainerFixedPosition();
    }
  }
/**
 * @return {?}
 */
private setFloatingDialogContainerAutoPosition() {
    let /** @type {?} */ clientWidth = document.getElementsByTagName('body')[0].clientWidth;
    let /** @type {?} */ clientX = parseInt(this.dialogOptions.properties.sourceEvent.pageX, 10);
    let /** @type {?} */ clientY = parseInt(this.dialogOptions.properties.sourceEvent.pageY, 10);
    let /** @type {?} */ innerHeight = window.innerHeight;

    if ((innerHeight - clientY) <= LIMIT_RANGE) {
      if ((clientWidth - clientX) <= LIMIT_RANGE) {
        this.dialogOptions.properties.position = 'leftUp';
      } else {
        this.dialogOptions.properties.position = 'rightUp';
      }
    } else {
      if ((clientWidth - clientX) <= LIMIT_RANGE) {
        this.dialogOptions.properties.position = 'leftBelow';
      } else {
        this.dialogOptions.properties.position = 'rightBelow';
      }
    }

    this.setFloatingDialogContainerFixedPosition();
  }
/**
 * @return {?}
 */
private setFloatingDialogContainerFixedPosition() {
    let /** @type {?} */ dialogContainer = this.getDialogContainer();

    let /** @type {?} */ clientWidth = document.getElementsByTagName('body')[0].clientWidth;
    let /** @type {?} */ scrollTop = document.getElementsByTagName('body')[0].getBoundingClientRect().top;
    let /** @type {?} */ clientX = parseInt(this.dialogOptions.properties.sourceEvent.pageX, 10);
    let /** @type {?} */ clientY = parseInt(this.dialogOptions.properties.sourceEvent.pageY, 10);
    let /** @type {?} */ innerHeight = window.innerHeight;
    let /** @type {?} */ newXCoords = { left: '', right: '' };
    let /** @type {?} */ newYCoords = { top: '', bottom: '' };

    newXCoords = this.checkCoordinatesLimitsX(
      clientWidth,
      dialogContainer.getBoundingClientRect().width,
      clientX,
      this.dialogOptions.properties.position.indexOf('left') !== -1 ? 'left' : 'right');

    dialogContainer.style.left = newXCoords.left;
    dialogContainer.style.right = newXCoords.right;

    setTimeout( _ => {
      newYCoords = this.checkCoordinatesLimitsY(
        scrollTop,
        innerHeight,
        dialogContainer.getBoundingClientRect().height,
        clientY,
        this.dialogOptions.properties.position.indexOf('Up') !== -1 ? 'up' : 'below');

      dialogContainer.style.top = newYCoords.top;
      dialogContainer.style.bottom = newYCoords.bottom;
    });
  }
/**
 * @param {?} clientWidth
 * @param {?} dialogWidth
 * @param {?} sourceX
 * @param {?} side
 * @return {?}
 */
private checkCoordinatesLimitsX(clientWidth, dialogWidth, sourceX, side) {
    let /** @type {?} */ left, /** @type {?} */ right;
    if (side === 'left') {
      if (sourceX < (dialogWidth + 8)) {
        left = '8px'; right = 'auto';
      } else {
        left = 'auto'; right = (clientWidth - sourceX) + 'px';
      }
    } else {
      if (clientWidth < (sourceX + dialogWidth + 8)) {
        left = 'auto'; right = '8px';
      } else {
        left = sourceX + 'px'; right = 'auto';
      }
    }
    return { left: left, right: right };
  }
/**
 * @param {?} scrollTop
 * @param {?} innerHeight
 * @param {?} dialogHeight
 * @param {?} sourceY
 * @param {?} direction
 * @return {?}
 */
private checkCoordinatesLimitsY(scrollTop, innerHeight, dialogHeight, sourceY, direction) {
    let /** @type {?} */ top, /** @type {?} */ bottom;
    if (direction === 'up') {
      if ((sourceY + scrollTop) < (dialogHeight + 8)) {
        top = (8 - scrollTop) + 'px'; bottom = 'auto';
      } else {
        top = 'auto'; bottom = (innerHeight - sourceY) + 'px';
      }
    } else {
      if (innerHeight < (sourceY + scrollTop + dialogHeight + 8)) {
        top = 'auto'; bottom = (8 + scrollTop) + 'px';
      } else {
        top = sourceY + 'px'; bottom = 'auto';
      }
    }
    return { top: top, bottom: bottom };
  }
/**
 * @return {?}
 */
private setDialogContainerInComponentPosition() {
    if (this.dialogOptions.properties.targetId) {
      let /** @type {?} */ body = document.getElementsByTagName('body')[0].getBoundingClientRect();
      let /** @type {?} */ target = document.getElementById(this.dialogOptions.properties.targetId);
      let /** @type {?} */ rect = target.getBoundingClientRect();
      let /** @type {?} */ dialogContainer = this.getDialogContainer();

      dialogContainer.style.left = parseInt('' + rect.left, 10) + 'px';
      dialogContainer.style.top = (parseInt('' + rect.top, 10) - body.top) + 'px';
      dialogContainer.style.width = parseInt('' + rect.width, 10) + 'px';
      dialogContainer.style.height = parseInt('' + rect.height, 10) + 'px';
      dialogContainer.style.position = 'absolute';
    }
  }
/**
 * @param {?} content
 * @return {?}
 */
private insertContentToDialog(content: any) {
    let /** @type {?} */ componentInstance = this.dialogRef.componentInstance;
    componentInstance.properties = this.dialogOptions.properties || null; // set CfDialog properties
    componentInstance.styling = this.dialogOptions.styling || null; // set CfDialog properties
  }
/**
 * @return {?}
 */
private setDialogCallbacks() {
    this.dialogRef.afterClosed().subscribe(result => {
      this.cleanDialogContainerStyle();

      if (result && this.dialogOptions[result.callback]) {
        this.dialogOptions[result.callback](result.callback);
      }
    });
  }
/**
 * @return {?}
 */
private cleanDialogContainerStyle() {
    // Have to clean AM2 overlay container style, because once added, this overlay DIV
    // is never removed and used by AM2 for all next Dialogs
    if (this.dialogContainer) {
      this.dialogContainer.removeAttribute('style');
    }
  }
/**
 * @return {?}
 */
private getDialogBackdropElement() {
    return this.dialogRef['_overlayRef']['_backdropElement'];
  }
/**
 * @return {?}
 */
private getDialogPane() {
    return this.dialogRef['_overlayRef']['_pane'];
  }
/**
 * @return {?}
 */
private getDialogPaneWrapper() {
    return this.getDialogPane().parentElement;
  }
/**
 * @return {?}
 */
private getDialogContainer() {
    return this.getDialogPaneWrapper().parentElement;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdDialog, },
];
}

function DialogService_tsickle_Closure_declarations() {
/** @type {?} */
DialogService.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DialogService.ctorParameters;
/** @type {?} */
DialogService.prototype.dialogRef;
/** @type {?} */
DialogService.prototype.dialogContainer;
/** @type {?} */
DialogService.prototype.dialogOptions;
/** @type {?} */
DialogService.prototype.dialog;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
